import React, { useState, useEffect, useContext } from 'react';
import LegoAdminPageContext from '../../../pages/legoAdminPageContext';
import { ModelGenerationsData } from '../../../../../model/generations/ModelGenerationsData.mjs';

// Global store to hold data and subscribers
const globalStore = {
  data: null,
  subscribers: [],
  updateListener: null,
};

// Empty instance used before data has loaded (and as the in-flight placeholder), so consumers can
// always call instance methods without null checks.
const EMPTY_MODELS_DATA = ModelGenerationsData.empty();

// Function to notify all subscribers about data changes
function notifySubscribers() {
  globalStore.subscribers.forEach(setData => setData(globalStore.data));
}


/**
 * Returns a `ModelGenerationsData` instance: the single source for models/generations data on the
 * client. Query it via its methods (gens/getGenDef/getBaseGenDef/makes/models/families/submodels/
 * modelTypeOf/modelsInLocale/submodelsOf/familyMembersOf/generations). Before the fetch resolves it
 * returns an empty instance, so methods are always safe to call.
 */
/** @returns {ModelGenerationsData} */
export default function useModelsData() {
  let { page } = useContext(LegoAdminPageContext);

  const [data, setData] = useState(() => globalStore.data || EMPTY_MODELS_DATA);

  useEffect(() => {
    let mounted = true;
    const safeSetData = (...args) => mounted && setData(...args);

    const fetchData = async () => {
      try {
        console.log('useModelsData: Fetching models data...');
        let res = await page.service('/services/data/models').find({ query: { byLocale: true } });

        // The whole compact payload is decoded by the class — no client-side shaping.
        globalStore.data = ModelGenerationsData.fromCompactData(res); // Store data in the global static variable.

        notifySubscribers(); // Notify all subscribers about data changes
      } catch (err) {
        console.error(err);
      }
    };

    if (!globalStore.data) {
      // Placeholder while the first fetch is in flight; also gates against duplicate fetches.
      globalStore.data = EMPTY_MODELS_DATA;
      // Make initial fetch of data
      fetchData();

      // Only add listener if it's the first subscriber.
      page.service('/services/data/models/gens').on('generations-changed', fetchData);
      console.log('%cMounted useModelsData', 'color: purple; font-weight: bold;');
      globalStore.updateListener = fetchData;
    }

    if (!globalStore.subscribers.length) {
    }

    // Register safeSetData as a subscriber (gated by mounted so unmount races don't fire setState)
    globalStore.subscribers.push(safeSetData);

    return () => {
      mounted = false;

      // Unregister safeSetData when component unmounts
      globalStore.subscribers = globalStore.subscribers.filter(func => func !== safeSetData);

      if (!globalStore.subscribers.length) {
        console.log('useModelsData: Clearing global store');
        globalStore.data = null;

        page.service('/services/data/models/gens').removeListener('generations-changed', globalStore.updateListener);
        console.log('%cUnmounted useModelsData', 'color: darkpurple; font-weight: bold;');
      }
    };
  }, []);

  return data;
}
